import AsyncStorage from '@react-native-async-storage/async-storage';
import { Character, PuzzleThemeCharacter } from './types';

export interface ChatMessage {
  id: string | number;
  characterId: string | number;
  content: string;
  timestamp: number;
  isUser: boolean;
  isSystem?: boolean;
}

export interface Chat {
  id: string;
  characterId: string;
  lastMessage: string;
  lastMessageTimestamp: number;
  unreadCount: number;
}

export interface CachedCharacterData {
  characters: PuzzleThemeCharacter[];
  timestamp: number;
  nextCursor?: string;
}

export class Database {
  private static instance: Database;
  private static CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  private static DEVICE_ID_KEY = 'device_id';
  private constructor() {}

  async getDeviceId(): Promise<string> {
    try {
      let deviceId = await AsyncStorage.getItem(Database.DEVICE_ID_KEY);
      if (!deviceId) {
        deviceId = `anonymous_${Date.now()}_${Math.random().toString(36).substring(2)}`;
        await AsyncStorage.setItem(Database.DEVICE_ID_KEY, deviceId);
      }
      return deviceId;
    } catch (error) {
      console.error('Error managing device ID:', error);
      return `fallback_${Date.now()}`;
    }
  }

  static getInstance(): Database {
    if (!Database.instance) {
      Database.instance = new Database();
    }
    return Database.instance;
  }

  async getChats(): Promise<Chat[]> {
    try {
      const chatsJson = await AsyncStorage.getItem('chats');
      return chatsJson ? JSON.parse(chatsJson) : [];
    } catch (error) {
      console.error('Error getting chats:', error);
      return [];
    }
  }

  async saveChat(chat: Chat): Promise<void> {
    try {
      const chats = await this.getChats();
      const existingIndex = chats.findIndex(c => c.id === chat.id);
      
      if (existingIndex >= 0) {
        chats[existingIndex] = chat;
      } else {
        chats.push(chat);
      }

      await AsyncStorage.setItem('chats', JSON.stringify(chats));
    } catch (error) {
      console.error('Error saving chat:', error);
    }
  }

  async getChatMessages(chatId: string): Promise<ChatMessage[]> {
    try {
      const messagesJson = await AsyncStorage.getItem(`messages_${chatId}`);
      return messagesJson ? JSON.parse(messagesJson) : [];
    } catch (error) {
      console.error('Error getting messages:', error);
      return [];
    }
  }

  async saveChatMessage(chatId: string, message: ChatMessage): Promise<void> {
    try {
      const messages = await this.getChatMessages(chatId);
      messages.push(message);
      await AsyncStorage.setItem(`messages_${chatId}`, JSON.stringify(messages));

      // Update the chat's last message
      const chat = (await this.getChats()).find(c => c.id === chatId);
      if (chat) {
        chat.lastMessage = message.content;
        chat.lastMessageTimestamp = message.timestamp;
        await this.saveChat(chat);
      }
    } catch (error) {
      console.error('Error saving message:', error);
    }
  }

  async getCachedCharacters(): Promise<CachedCharacterData | null> {
    try {
      // const cachedData = await AsyncStorage.getItem('cached_characters');
      const cachedData = await AsyncStorage.getItem('cached_puzzle_characters');
      if (!cachedData) return null;
      
      const data: CachedCharacterData = JSON.parse(cachedData);
      const isExpired = Date.now() - data.timestamp > Database.CACHE_EXPIRY;
      
      return isExpired ? null : data;
    } catch (error) {
      console.error('Error getting cached characters:', error);
      return null;
    }
  }

  async cacheCharacters(characters: PuzzleThemeCharacter[], nextCursor?: string): Promise<void> {
    try {
      const existingCache = await this.getCachedCharacters();
      const newCache: CachedCharacterData = {
        characters: existingCache ? [...existingCache.characters, ...characters] : characters,
        timestamp: Date.now(),
        nextCursor
      };
      // await AsyncStorage.setItem('cached_characters', JSON.stringify(newCache));
      await AsyncStorage.setItem('cached_puzzle_characters', JSON.stringify(newCache));
    } catch (error) {
      console.error('Error caching characters:', error);
    }
  }

  async clearCharacterCache(): Promise<void> {
    try {
      // await AsyncStorage.removeItem('cached_characters');
      await AsyncStorage.removeItem('cached_puzzle_characters');
    } catch (error) {
      console.error('Error clearing character cache:', error);
    }
  }
}

export const db = Database.getInstance();